<template>
    <v-container fluid class="mt-10 pb-12" style="min-width: 1200px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">
        <!-- Header Section -->
        <div class="overtime-header mb-6">
            <div class="d-flex align-center justify-space-between mb-4">
                <div>
                    <h1 class="text-h4 font-weight-bold mb-2">Overtime Management</h1>
                    <p class="text-subtitle-1 grey--text mb-0">View and manage overtime requests across all teams</p>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <v-skeleton-loader v-if="loading" type="card, card, card"></v-skeleton-loader>

        <template v-else>
            <!-- Statistics Cards -->
            <v-row class="mb-6">
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4" elevation="1" style="background-color: #FFF7ED;">
                        <div class="d-flex align-center mb-2">
                            <v-icon class="mr-2" color="orange">mdi-clock-outline</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">Pending</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ pendingCount }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4" elevation="1" style="background-color: #F0FDF4;">
                        <div class="d-flex align-center mb-2">
                            <v-icon class="mr-2" color="green">mdi-check-circle</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">Approved</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ approvedCount }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4" elevation="1" style="background-color: #FEF2F2;">
                        <div class="d-flex align-center mb-2">
                            <v-icon class="mr-2" color="red">mdi-close-circle</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">Declined</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ declinedCount }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4" elevation="1" style="background-color: #F5F3FF;">
                        <div class="d-flex align-center mb-2">
                            <v-icon class="mr-2" color="purple">mdi-timer-outline</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">Total Requests</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ overtimeRequests.length }}</div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Search & Filter -->
            <v-row class="mb-4">
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model="searchQuery"
                        placeholder="Search employees..."
                        outlined
                        dense
                        hide-details
                        prepend-inner-icon="mdi-magnify"
                        clearable
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-select
                        v-model="statusFilter"
                        :items="filterOptions"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        hide-details
                        label="Filter by status"
                        prepend-inner-icon="mdi-filter"
                    />
                </v-col>
            </v-row>

            <!-- Department Grouped Overtime Requests -->
            <div v-if="filteredDepartments.length === 0" class="text-center py-8">
                <v-icon size="64" color="grey lighten-1">mdi-timer-off-outline</v-icon>
                <p class="text-h6 grey--text mt-4">No overtime requests found</p>
            </div>

            <div v-for="department in filteredDepartments" :key="department.name" class="department-section mb-4">
                <v-card elevation="1" class="department-card">
                    <!-- Department Header -->
                    <div class="department-header pa-4" style="background-color: #F8F9FA; cursor: pointer;" @click="toggleDepartment(department.name)">
                        <div class="d-flex align-center justify-space-between">
                            <div class="d-flex align-center">
                                <v-icon small class="mr-2">{{ isDepartmentExpanded(department.name) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                                <div :style="{ width: '4px', height: '24px', backgroundColor: department.color, marginRight: '12px' }"></div>
                                <span class="text-h6 font-weight-bold">{{ department.name }}</span>
                                <v-chip small class="ml-2">{{ department.employees.length }}</v-chip>
                            </div>
                        </div>
                    </div>

                    <!-- Collapsible Content -->
                    <v-expand-transition>
                        <div v-show="isDepartmentExpanded(department.name)">
                            <div v-for="employee in department.employees" :key="employee.id" class="employee-item">
                                <div class="employee-row pa-4" :class="{ 'expanded': expandedEmployee === employee.id }" @click="toggleEmployeeExpand(employee.id)">
                                    <div class="d-flex align-center" style="flex: 2; min-width: 250px;">
                                        <v-icon small class="mr-2">{{ expandedEmployee === employee.id ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                                        <v-avatar size="32" color="primary" class="mr-3">
                                            <span class="white--text text-caption">{{ getInitials(employee.name) }}</span>
                                        </v-avatar>
                                        <span class="font-weight-medium">{{ employee.name }}</span>
                                    </div>
                                    <div style="flex: 3; min-width: 300px;" class="d-flex align-center" @click.stop>
                                        <v-chip x-small class="mr-1" outlined>{{ employee.requests.length }} request{{ employee.requests.length !== 1 ? 's' : '' }}</v-chip>
                                        <v-chip v-if="employee.pendingCount > 0" x-small color="orange" text-color="white" class="mr-1">{{ employee.pendingCount }} pending</v-chip>
                                        <v-chip v-if="employee.approvedCount > 0" x-small color="green" text-color="white" class="mr-1">{{ employee.approvedCount }} approved</v-chip>
                                        <v-chip v-if="employee.declinedCount > 0" x-small color="red" text-color="white">{{ employee.declinedCount }} declined</v-chip>
                                    </div>
                                </div>

                                <!-- Expanded Overtime Requests -->
                                <v-expand-transition>
                                    <div v-if="expandedEmployee === employee.id" class="overtime-history-section pa-4" style="background-color: #F8F9FA;">
                                        <v-simple-table dense>
                                            <template v-slot:default>
                                                <thead>
                                                    <tr>
                                                        <th>Planned Date</th>
                                                        <th>Allocated Time</th>
                                                        <th>Tasks</th>
                                                        <th>Approver</th>
                                                        <th>Status</th>
                                                        <th>Requested</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="request in employee.requests" :key="request.id">
                                                        <td>{{ formatDate(request.planned_data) }}</td>
                                                        <td>{{ request.overtime }}</td>
                                                        <td>
                                                            <div v-if="request.overtime_data && request.overtime_data.length" class="overtime-tasks-cell">
                                                                <div v-for="task in request.overtime_data" :key="task.id" class="overtime-task-line">
                                                                    <span class="font-weight-medium">{{ task.task?.name || 'Unknown task' }}</span>
                                                                    <span v-if="task.task?.company?.name" class="grey--text text-caption"> ({{ task.task.company.name }})</span>
                                                                    <div v-if="task.note_per_task" class="text-caption grey--text font-italic">{{ task.note_per_task }}</div>
                                                                </div>
                                                            </div>
                                                            <span v-else class="grey--text">No tasks</span>
                                                        </td>
                                                        <td>{{ request.overtime_approver?.name || 'No approver' }}</td>
                                                        <td>
                                                            <v-chip x-small :color="getStatusColor(request)" text-color="white">
                                                                {{ request.overtime_status?.name || 'Unknown' }}
                                                            </v-chip>
                                                        </td>
                                                        <td>{{ formatDate(request.created_at) }}</td>
                                                        <td>
                                                            <div v-if="isPending(request)" class="d-flex" style="gap: 4px;">
                                                                <v-btn
                                                                    x-small
                                                                    color="success"
                                                                    outlined
                                                                    :loading="updatingId === request.id"
                                                                    @click="approveRequest(request)"
                                                                >
                                                                    Approve
                                                                </v-btn>
                                                                <v-btn
                                                                    x-small
                                                                    color="error"
                                                                    outlined
                                                                    :loading="updatingId === request.id"
                                                                    @click="declineRequest(request)"
                                                                >
                                                                    Decline
                                                                </v-btn>
                                                            </div>
                                                            <span v-else class="grey--text text-caption">&mdash;</span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </template>
                                        </v-simple-table>
                                    </div>
                                </v-expand-transition>
                            </div>
                        </div>
                    </v-expand-transition>
                </v-card>
            </div>
        </template>
    </v-container>
</template>

<script>
export default {
    name: "OvertimeManagementComponent",
    data() {
        return {
            loading: false,
            searchQuery: '',
            statusFilter: 'all', // all, pending, approved, declined
            expandedDepartments: [],
            expandedEmployee: null,
            overtimeRequests: [], // all overtime requests, all users
            overtimeStatuses: [], // [{id, name}]
            updatingId: null,
            departmentColors: {
                'Engineering': '#2196F3',
                'Management': '#4CAF50',
                'Design': '#FF9800',
                'Analytics': '#9C27B0',
                'Sales': '#F44336',
                'Marketing': '#E91E63',
                'HR': '#00BCD4',
                'Finance': '#009688',
                'Operations': '#FF5722',
                'Product': '#673AB7',
                'Other': '#9E9E9E'
            }
        };
    },
    computed: {
        filterOptions() {
            return [
                { text: 'All Requests', value: 'all' },
                { text: 'Pending', value: 'pending' },
                { text: 'Approved', value: 'approved' },
                { text: 'Declined', value: 'declined' }
            ];
        },

        approvedStatusId() {
            const status = this.overtimeStatuses.find(s => (s.name || '').toLowerCase().includes('approv'));
            return status ? status.id : 2;
        },

        declinedStatusId() {
            const status = this.overtimeStatuses.find(s => (s.name || '').toLowerCase().includes('declin'));
            return status ? status.id : 3;
        },

        pendingCount() {
            return this.overtimeRequests.filter(r => (r.overtime_status?.name || '').toLowerCase().includes('pending')).length;
        },

        approvedCount() {
            return this.overtimeRequests.filter(r => (r.overtime_status?.name || '').toLowerCase().includes('approv')).length;
        },

        declinedCount() {
            return this.overtimeRequests.filter(r => (r.overtime_status?.name || '').toLowerCase().includes('declin')).length;
        },

        // Group requests by requester so employees who were deactivated/deleted
        // after submitting a request still show up (keyed by user_id, not the
        // separately-loaded active users list)
        employeesByRole() {
            const grouped = {};

            this.overtimeRequests.forEach(request => {
                const requester = request.user;
                const roleName = requester?.role?.name || (requester ? 'Other' : 'Unknown / Deleted User');
                const employeeKey = request.user_id;

                if (!grouped[roleName]) {
                    grouped[roleName] = {};
                }
                if (!grouped[roleName][employeeKey]) {
                    grouped[roleName][employeeKey] = {
                        id: employeeKey,
                        name: requester ? this.getUserFullName(requester) : `Deleted user (ID: ${employeeKey})`,
                        requests: []
                    };
                }
                grouped[roleName][employeeKey].requests.push(request);
            });

            return grouped;
        },

        // Convert grouped requests into department structure, applying search + status filters
        filteredDepartments() {
            return Object.keys(this.employeesByRole).map(roleName => {
                const employees = Object.values(this.employeesByRole[roleName])
                    .filter(employee => {
                        if (!this.searchQuery) return true;
                        return employee.name.toLowerCase().includes(this.searchQuery.toLowerCase());
                    })
                    .map(employee => {
                        const allRequests = [...employee.requests].sort((a, b) => b.id - a.id);
                        const requests = allRequests.filter(r => this.matchesStatusFilter(r));

                        return {
                            id: employee.id,
                            name: employee.name,
                            requests,
                            pendingCount: allRequests.filter(r => (r.overtime_status?.name || '').toLowerCase().includes('pending')).length,
                            approvedCount: allRequests.filter(r => (r.overtime_status?.name || '').toLowerCase().includes('approv')).length,
                            declinedCount: allRequests.filter(r => (r.overtime_status?.name || '').toLowerCase().includes('declin')).length,
                        };
                    })
                    .filter(employee => employee.requests.length > 0);

                return {
                    name: roleName,
                    color: this.departmentColors[roleName] || this.departmentColors['Other'],
                    employees
                };
            }).filter(department => department.employees.length > 0);
        }
    },
    async mounted() {
        await this.loadData();
    },
    methods: {
        async loadData() {
            try {
                this.loading = true;
                await Promise.all([
                    this.loadOvertimeOverview(),
                    this.loadOvertimeStatuses()
                ]);
            } catch (error) {
                console.error('[OvertimeManagement] Error loading data:', error);
                this.$toast.error('Failed to load data');
            } finally {
                this.loading = false;
            }
        },

        async loadOvertimeOverview() {
            try {
                const response = await this.$axios.get('api/overtime/overview');
                this.overtimeRequests = response.data || [];
            } catch (error) {
                console.error('[OvertimeManagement] Error fetching overtime overview:', error);
            }
        },

        async loadOvertimeStatuses() {
            try {
                const response = await this.$axios.get('api/overtime/status');
                this.overtimeStatuses = response.data || [];
            } catch (error) {
                console.error('[OvertimeManagement] Error fetching overtime statuses:', error);
            }
        },

        matchesStatusFilter(request) {
            if (this.statusFilter === 'all') return true;
            const name = (request.overtime_status?.name || '').toLowerCase();
            if (this.statusFilter === 'pending') return name.includes('pending');
            if (this.statusFilter === 'approved') return name.includes('approv');
            if (this.statusFilter === 'declined') return name.includes('declin');
            return true;
        },

        isPending(request) {
            return (request.overtime_status?.name || '').toLowerCase().includes('pending');
        },

        getStatusColor(request) {
            const name = (request.overtime_status?.name || '').toLowerCase();
            if (name.includes('pending')) return 'orange';
            if (name.includes('approv')) return 'green';
            if (name.includes('declin')) return 'red';
            return 'grey';
        },

        async approveRequest(request) {
            await this.updateOvertimeStatus(request, this.approvedStatusId);
        },

        async declineRequest(request) {
            await this.updateOvertimeStatus(request, this.declinedStatusId);
        },

        async updateOvertimeStatus(request, statusId) {
            this.updatingId = request.id;
            try {
                await this.$axios.patch(`api/overtime/${request.id}`, { overtime_status: statusId });
                this.$toast.success('Overtime request updated successfully');
                await this.loadOvertimeOverview();
            } catch (error) {
                console.error('[OvertimeManagement] Error updating overtime status:', error);
                this.$toast.error('Failed to update overtime request');
            } finally {
                this.updatingId = null;
            }
        },

        getUserFullName(user) {
            if (!user) return 'Unknown User';
            if (user.first_name || user.last_name) {
                return `${user.first_name || ''} ${user.last_name || ''}`.trim();
            }
            return user.name || user.email || 'Unknown User';
        },

        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(word => word[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },

        toggleEmployeeExpand(employeeId) {
            this.expandedEmployee = this.expandedEmployee === employeeId ? null : employeeId;
        },

        toggleDepartment(departmentName) {
            const index = this.expandedDepartments.indexOf(departmentName);
            if (index > -1) {
                this.expandedDepartments.splice(index, 1);
            } else {
                this.expandedDepartments.push(departmentName);
            }
        },

        isDepartmentExpanded(departmentName) {
            return this.expandedDepartments.includes(departmentName);
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            return this.$date(dateString).format('MMM DD, YYYY');
        }
    }
};
</script>

<style lang="scss" scoped>
.department-section {
    .department-card {
        border-radius: 12px;
        overflow: hidden;
    }

    .department-header {
        border-bottom: 1px solid #E0E0E0;
    }
}

.employee-row {
    display: flex;
    align-items: center;
    border-bottom: 1px solid #F0F0F0;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #FAFAFA;
    }

    &.expanded {
        background-color: #F8F9FA;
    }
}

.overtime-history-section {
    border-top: 1px solid #E0E0E0;
}

.overtime-tasks-cell {
    max-width: 320px;
}

.overtime-task-line {
    margin-bottom: 4px;

    &:last-child {
        margin-bottom: 0;
    }
}

.v-card {
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }
}
</style>
